import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { AdditionalEducatorAssignment, InterventionGroup, InterventionGroupAssignment } from "../../../store/onboarding/cases/types";
import { getFullName, isShowConfimationDialog } from "../../../utils/NamesUtils";
import Table from "react-bootstrap/Table";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { PopupDismissalKeys, ReactSelectOption, TeacherInfo } from "../../../store/onboarding/types";
import { UserAssignmentRole, UserInfo } from "../../../store/auth/types";
import Select from "react-select";
import { getTeachers } from "../../../store/onboarding/actions";
import { ValueType } from "react-select/src/types";
import { patchInterventionGroup } from "../../../store/onboarding/cases/actions";
import { Spinner } from "react-bootstrap";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toastr } from "react-redux-toastr";
import { showConfirmDialog } from "../../../store/confirm-dialog/actions";
import useUserRole from "../../../utils/hooks/useUserRole";
import AdditionalEducatorsSelector from "../../common/reading-interventions/intervention-tools/recommend-groups/group-card/AdditionalEducatorsSelector";
import useHighlightGroupCard from "../../common/reading-interventions/intervention-tools/recommend-groups/hooks/useHighlightGroupCard";

type OwnProps = {
  interventionGroup: InterventionGroup;
  isStudentDetail?: boolean;
  isGroupDetail?: boolean;
};

type Props = OwnProps;

const InterventionGroupAssignmentsTable: FunctionComponent<Props> = (props) => {
  const [localInterventionGroup, setLocalInterventionGroup] = useState<any>();
  const { interventionGroup, isStudentDetail, isGroupDetail } = props;
  let [additionalEducatorPlan, setAdditionalEducatorPlan] = useState<any>([]);
  
  const { isCoach, isTeacher } = useUserRole();
  
  const teachersRoaster = useSelector<ApplicationState, TeacherInfo[]>(
    (s) => s.onboarding.teachersRoster
  );

  const dispatch = useDispatch();
  const loadingGetTeachers = useSelector<ApplicationState, boolean | undefined>(
    (s) => s.onboarding.isLoading.getTeachers
  );
  useEffect(() => {
    // Only fetch teachers if roster is empty and we're not already loading
    if ((!teachersRoaster || teachersRoaster.length === 0) && !loadingGetTeachers) {
      dispatch(getTeachers());
    }

    // Keep local state in sync when parent prop changes. This avoids stale local state
    // and prevents resets caused by unrelated store updates (e.g. teachers fetch).
    setLocalInterventionGroup(interventionGroup);
    if (interventionGroup?.additional_educator_plan != null) {
      setAdditionalEducatorPlan(interventionGroup.additional_educator_plan);
    } else {
      setAdditionalEducatorPlan([]);
    }
  }, [dispatch, teachersRoaster, loadingGetTeachers, interventionGroup]);

  useEffect(() => {
    //console.log('additionalEducatorPlan', additionalEducatorPlan)
  },[additionalEducatorPlan]);

  const loadingPatchInterventionGroup = useSelector<
    ApplicationState,
    boolean | undefined
  >((s) => s.cases.isLoading.patchInterventionGroup);

  const { highlightGroup } = useHighlightGroupCard();

  useEffect(() => {
    if (
      !loadingPatchInterventionGroup &&
      localInterventionGroup !== interventionGroup
    ) {
      highlightGroup(
        `employee_assignment_${isTeacher ? interventionGroup?.coach_assignment?.user.id :interventionGroup?.teacher_assignment?.user.id}`,
        interventionGroup?.id
      );
    }
  }, [loadingPatchInterventionGroup]);


  const currentUser: UserInfo = useSelector(
    (s: ApplicationState) => s.auth.userInfo!
  );

  const teachers = useMemo(() => {
    return teachersRoaster
      .filter((t) => t.user.profile.current_role === UserAssignmentRole.TEACHER)
      .map((t) => t.user);
  }, [teachersRoaster]);

  const [showTeacherSelector, setShowTeacherSelector] = useState<boolean>(
    false
  );

  const userDismissPopupDetails =
                useSelector(
                  (s: ApplicationState) => s.auth.userInfo?.user_dismiss_pop_up_details || {}
                );

  const handleSelectedTeacherChange = (value: ValueType<UserInfo, false>) => {
    let isNotExistInAdditionalEducator = !interventionGroup.associated_assignments
    .some((associateAssign: InterventionGroupAssignment) => 
      interventionGroup.teacher_assignment?.user.id == associateAssign.user.id);
    const shouldShowDialog =
      (isNotExistInAdditionalEducator ||
      !interventionGroup.associated_assignments.length) &&
      isShowConfimationDialog(userDismissPopupDetails[PopupDismissalKeys.TEACHER_CHANGE_IN_GROUP]);

    const confirmText = isCoach
      ? <>
      <p className="font-weight-bold">Do you want to change the interventionist without assigning them as an Additional Educator?</p>
      <p>If not assigned, the current interventionist will lose access to this group.</p>
      </>
      : isTeacher
      ? <>
      <p className="font-weight-bold">Do you want to change the interventionist without assigning yourself as an Additional Educator?</p>
      <p>If not assigned, you will lose access to this group.</p>
      </>
      : "";

    const patchGroup = () => {
      dispatch(
        patchInterventionGroup(interventionGroup.id, {
          teacher_assignment: value?.profile.current_assignment?.id,
        }, false, true)
      );
    };

    if (shouldShowDialog) {
      dispatch(
      showConfirmDialog({
        title: "Change Interventionist",
        text: confirmText,
        centered: true,
        onConfirm: patchGroup,
        confirmButtonText: "YES, CHANGE INTERVENTIONIST",
        cancelButtonText: "CANCEL",
        timerElipseLeft: true,
        countDownTimer: 5,
        popupKey: PopupDismissalKeys.TEACHER_CHANGE_IN_GROUP,
      })
      );
    } else {
      patchGroup();
    }
  };


  return (
    <Table className="goalTable table-bordered">
      <thead>
        <tr>
          <th className="group-workspace-additional">Teacher</th>
          <th>Additional Educator(s)
            {loadingPatchInterventionGroup && (
              <Spinner className="float-right" animation="border" size="sm" />
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            {showTeacherSelector ? (
              <Select
                autoFocus
                className="-m-2"
                isLoading={loadingGetTeachers}
                onMenuClose={() => setShowTeacherSelector(false)}
                defaultMenuIsOpen
                options={teachers.filter((teacher) => {
                  if(currentUser.allowed_assigned_teachers_ids !== undefined) {
                    if(currentUser.allowed_assigned_teachers_ids.length) {
                      return currentUser.allowed_assigned_teachers_ids.includes(teacher.id) 
                    } else {
                      return true;
                    }
                  } else {
                    return false;
                  }
                })}
                value={interventionGroup.teacher_assignment?.user}
                getOptionValue={(t) => t.id.toString()}
                getOptionLabel={(t) => getFullName(t)}
                onChange={handleSelectedTeacherChange}
              />
            ) : (
              <>
                {getFullName(interventionGroup.teacher_assignment?.user)}
                {!isStudentDetail && !isGroupDetail ? <span
                  className="pointer"
                  onClick={() => setShowTeacherSelector(true)}
                >
                  <FontAwesomeIcon icon={faEdit} className="ml-2" />
                </span> : ''}
              </>
            )}
          </td>
          <td colSpan={2}>
            <AdditionalEducatorsSelector 
              interventionGroup={interventionGroup}
            />
          </td>
        </tr>
      </tbody>
    </Table>
  );
};

export default InterventionGroupAssignmentsTable;
